import { ApiResponse, ImportResult, TransactionDashboardData } from "../types";
import { TransactionAttributes, TransactionCreatePayload } from "../types";
import { apiClient } from "./apiClient";
import { authService } from "./authService";
import { API_BASE_URL } from "./config";

const BASE = "/transactions";

function buildUrl(
  path: string,
  params?: Record<string, string | number>
): string {
  if (!params || Object.keys(params).length === 0) return path;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => search.set(k, String(v)));
  return `${path}?${search.toString()}`;
}

/** Body for POST /transactions/transfer — cria saída na origem e entrada no destino. */
export interface TransferPayload {
  companyId: number;
  amount: number;
  originBankAccountId: number;
  destinationBankAccountId: number;
  paymentMethod?: string | null;
  description?: string | null;
  observation?: string | null;
  competenceDate?: string | null;
  dueDate?: string | null;
  paymentDate?: string | null;
  product?: string | null;
  client?: string | null;
  costCenterId?: number | null;
}

export interface GetAllTransactionsFilters {
  paymentDateFrom?: string;   // YYYY-MM-DD
  paymentDateTo?: string;    // YYYY-MM-DD
  competenceDateFrom?: string; // YYYY-MM-DD
  competenceDateTo?: string;   // YYYY-MM-DD
  dueDateFrom?: string;      // YYYY-MM-DD
  dueDateTo?: string;        // YYYY-MM-DD
}

export const transactionsApi = {
  async getAll(
    companyId?: number,
    filters?: GetAllTransactionsFilters
  ): Promise<ApiResponse<TransactionAttributes[]>> {
    try {
      const params: Record<string, string | number> =
        companyId != null ? { companyId } : {};
      if (filters?.paymentDateFrom?.trim()) params.paymentDateFrom = filters.paymentDateFrom.trim();
      if (filters?.paymentDateTo?.trim()) params.paymentDateTo = filters.paymentDateTo.trim();
      if (filters?.competenceDateFrom?.trim()) params.competenceDateFrom = filters.competenceDateFrom.trim();
      if (filters?.competenceDateTo?.trim()) params.competenceDateTo = filters.competenceDateTo.trim();
      if (filters?.dueDateFrom?.trim()) params.dueDateFrom = filters.dueDateFrom.trim();
      if (filters?.dueDateTo?.trim()) params.dueDateTo = filters.dueDateTo.trim();
      const url = Object.keys(params).length > 0 ? buildUrl(BASE, params) : BASE;
      const data = await apiClient.get<any>(url);
      return {
        success: true,
        data: Array.isArray(data)
          ? data
          : (data?.data ?? data?.transactions ?? []),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async getById(id: number): Promise<ApiResponse<TransactionAttributes>> {
    try {
      const data = await apiClient.get<any>(`${BASE}/${id}`);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /** GET /transactions/dashboard — relatório por competência (plan group → categorias). */
  async getDashboard(
    companyId: number,
    filters: { competenceDateFrom: string; competenceDateTo: string }
  ): Promise<ApiResponse<TransactionDashboardData>> {
    try {
      const url = buildUrl(`${BASE}/dashboard`, {
        companyId,
        competenceDateFrom: filters.competenceDateFrom.trim(),
        competenceDateTo: filters.competenceDateTo.trim(),
      });
      const json = await apiClient.get<any>(url);
      const data = json?.data ?? json;
      return {
        success: true,
        data: data as TransactionDashboardData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /**
   * POST /transactions/transfer — transferência entre contas (saída + entrada no servidor).
   */
  async transfer(
    payload: TransferPayload
  ): Promise<ApiResponse<unknown>> {
    try {
      const data = await apiClient.post<any>(`${BASE}/transfer`, payload);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async create(
    payload: TransactionCreatePayload
  ): Promise<ApiResponse<TransactionAttributes>> {
    try {
      const data = await apiClient.post<any>(BASE, payload);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async update(
    id: number,
    payload: Partial<
      Omit<
        TransactionAttributes,
        "id" | "companyId" | "createdAt" | "updatedAt"
      >
    >
  ): Promise<ApiResponse<TransactionAttributes>> {
    try {
      const data = await apiClient.put<any>(`${BASE}/${id}`, payload);
      return {
        success: true,
        data: data?.data ?? data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  async delete(id: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /**
   * PUT /api/transactions/bulk
   * Update multiple transactions with the same fields. Body: { transactionIds: number[], ...updateFields }
   */
  async updateBulk(
    transactionIds: number[],
    payload: Partial<
      Omit<
        TransactionAttributes,
        "id" | "companyId" | "createdAt" | "updatedAt"
      >
    >
  ): Promise<ApiResponse<TransactionAttributes[]>> {
    try {
      const data = await apiClient.put<any>(`${BASE}/bulk`, {
        transactionIds,
        ...payload,
      });
      return {
        success: true,
        data: Array.isArray(data?.data) ? data.data : data?.transactions ?? [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  /**
   * POST /api/transactions/import
   * Import transactions from Excel (xlsx). First sheet must have headers:
   * Categoria, Conta, Valor, Forma de Pagamento, Parcela, Tipo, Ref Competencia,
   * Data Vencimento, Data Pagamento, Descricao, Produto ou Servico, Cliente ou Fornecedor,
   * Centro de Custo, Observação
   * Returns: { message, data: ImportResult } or legacy { created, failed, errors }
   * Multipart: file (xlsx), companyId (required)
   */
  async importFromExcel(
    companyId: number,
    file: File
  ): Promise<ApiResponse<ImportResult>> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("companyId", String(companyId));

      const token = authService.getToken();
      const headers: HeadersInit = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${API_BASE_URL}${BASE}/import`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (response.status === 401) {
        authService.logout();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        if (contentType?.includes("application/json")) {
          try {
            const err = await response.json();
            errorMessage = err.error ?? err.message ?? errorMessage;
          } catch {
            // use default
          }
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get("content-type");
      const json =
        contentType?.includes("application/json")
          ? await response.json()
          : {};
      const data = json?.data ?? json;
      const result: ImportResult = data?.entities
        ? { entities: data.entities, errors: Array.isArray(data?.errors) ? data.errors : [] }
        : {
            entities: {
              transactions: {
                created: data?.created ?? 0,
                failed: data?.failed ?? 0,
              },
            },
            errors: Array.isArray(data?.errors) ? data.errors : [],
          };
      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },
};
